
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/fdabe7c7ccee11942d3988daf4f91aee/59b67839594dbf477d0abb69e6082d78.png" 
              alt="UmiGo Logo" 
              className="h-10 w-auto"
            />
          </Link>

          <div className="relative flex items-center gap-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="w-10 h-10 flex items-center justify-center bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-gray-100 rounded-full hover:bg-white/20 dark:hover:bg-gray-700/50 transition-all cursor-pointer border border-gray-200 dark:border-gray-700"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <i className="ri-sun-line text-lg"></i>
              ) : (
                <i className="ri-moon-line text-lg"></i>
              )}
            </button>

            {/* Language Switcher */}
            <div className="relative">
              <button 
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="px-4 py-2.5 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-gray-100 rounded-full font-semibold hover:bg-white/20 dark:hover:bg-gray-700/50 transition-all whitespace-nowrap text-sm cursor-pointer flex items-center gap-2 border border-gray-200 dark:border-gray-700"
              >
                <span>{currentLanguage.flag}</span>
                <span>{currentLanguage.code.toUpperCase()}</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>

              {showLanguageMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowLanguageMenu(false)}
                  ></div>
                  <div className="absolute top-full right-0 mt-3 w-48 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-100 dark:border-gray-700">
                    <div className="p-2">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                            i18n.language === lang.code ? 'bg-teal-50 dark:bg-teal-900/30' : ''
                          }`}
                        >
                          <span className="text-2xl">{lang.flag}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{lang.name}</span>
                          {i18n.language === lang.code && (
                            <i className="ri-check-line text-teal-600 dark:text-teal-400 ml-auto"></i>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Download Button */}
            <button 
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="px-6 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full font-semibold hover:shadow-lg transition-all whitespace-nowrap text-sm cursor-pointer"
            >
              {t('nav.getUmiGo')}
            </button>

            {showDownloadMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowDownloadMenu(false)}
                ></div>
                <div className="absolute top-full right-0 mt-3 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-100 dark:border-gray-700">
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-3">{t('nav.downloadTitle')}</h3>
                    
                    <a 
                      href="https://umigo.cc/umigo_v1.0.1.apk" 
                      download
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group"
                      onClick={() => setShowDownloadMenu(false)}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-400 to-green-600 rounded-xl">
                        <i className="ri-android-line text-white text-xl"></i>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">{t('nav.apkDownload')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('nav.apkDesc')}</div>
                      </div>
                      <i className="ri-download-2-line text-gray-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors"></i>
                    </a>

                    <a 
                      href="#" 
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDownloadMenu(false);
                      }}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl">
                        <i className="ri-google-play-line text-white text-xl"></i>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">{t('nav.googlePlay')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('nav.googlePlayDesc')}</div>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors"></i>
                    </a>

                    <a 
                      href="#" 
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDownloadMenu(false);
                      }}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl">
                        <i className="ri-apple-line text-white text-xl"></i>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">{t('nav.appStore')}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('nav.appStoreDesc')}</div>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors"></i>
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
